import { onBeforeUnmount, onMounted, watch, type Ref } from 'vue';

/**
 * Progressive loading for paginated lists: observes a sentinel element near
 * the end of the list and calls `loadMore` whenever it scrolls into view.
 *
 * The visible "Show More" button stays in the template as the fallback —
 * if IntersectionObserver is unavailable (or fails to fire), the button
 * still works exactly as before. `loadMore` must be safe to call when the
 * list is exhausted (both list pages already guard via their `hasMore`
 * computed).
 */
export function useInfiniteScroll(
  sentinel: Ref<HTMLElement | null>,
  loadMore: () => void,
) {
  let observer: IntersectionObserver | null = null;

  onMounted(() => {
    // No observer support -> silently keep the button-only behaviour.
    if (!('IntersectionObserver' in window)) return;

    observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          loadMore();
          // Re-observe so the next evaluation fires even when the sentinel
          // never left the rootMargin between appends (tall viewports would
          // otherwise stall until the user scrolls again).
          if (sentinel.value && observer) {
            observer.unobserve(sentinel.value);
            requestAnimationFrame(() => {
              if (sentinel.value && observer) observer.observe(sentinel.value);
            });
          }
        }
      },
      // Start loading slightly before the sentinel is actually visible so
      // the next page is usually ready by the time the user reaches it.
      { rootMargin: '200px 0px' },
    );

    // The sentinel is rarely in the DOM at mount — it's usually gated on data
    // that hasn't arrived yet (`v-if="hasMore"`), and inside a dialog the
    // content is portaled a tick later still. Observing only here would attach
    // to nothing and the list would never page. Track the ref instead, so the
    // sentinel is picked up whenever it appears (and re-attached if it's
    // replaced).
    watch(
      sentinel,
      (element, previous) => {
        if (previous) observer?.unobserve(previous);
        if (element) observer?.observe(element);
      },
      { immediate: true },
    );
  });

  onBeforeUnmount(() => {
    observer?.disconnect();
    observer = null;
  });
}
